import cn from "classnames";
import { useIntl } from "react-intl";

// Styles
import useStyles from "./HubManagement.styles";

// Images
import drax_lighthouse_animation from "assets/animations/drax_lighthouse_animation.gif";

// Icons
import icons from "enums/icons";

// Utils
import { format } from "date-fns";

// MUI components
import {
    Box,
    Chip,
    IconButton,
    FormLabel,
    InputAdornment,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
    ListItemText,
    MenuItem,
    Skeleton,
    Select,
} from "@mui/material";

// Hooks
import useHubManagement from "./useHubManagement";

// Components
import { TagName } from "components/TagName";
import CustomIcon from "components/CustomIcon";
import { ClearFilterButton } from "components/Buttons";
import { SnackBar } from "components/SnackBar";
import SortMenu from "components/SortMenu/SortMenu";
import NavButton from "components/HeaderNavigation/NavButton/NavButton";
import { ButtonWithCounter } from "components/Buttons/ButtonWithCounter";
import { Header, Body } from "components/Layout/LayoutContent/LayoutContent";
import { ListOfEntities } from "components/SnackBar/ListOfEntities/ListOfEntities";
import { HideColumnsMenu } from "components/HideColumnsMenu";
import { Checkbox } from "components/Checkbox";
import TablePagination from "components/Talent/TablePagination";
import { isEmpty } from "lodash";

const HubManagement = () => {
    const intl = useIntl();
    const classes = useStyles();

    const {
        hubs,
        filters,
        shouldOpenSnackBar,
        popOverAnchorEl,
        tableHeader,
        currentSort,
        rowId,
        hidableColumns,
        visibleColumns,
        disableAll,
        disableClearAll,
        ldPermissionsData,
        pacePermissionsData,
        inputName,
        inputPage,
        paginationTextInputRef,
        usersData,
        handleChangeFilters,
        handleClearFilters,
        handleSort,
        handleClickCounter,
        handleClickCloseSnackbar,
        handleToggleColumn,
        handlePageChange,
        handleInputChange,
        handleOnBlur,
        handleOnKeyDown,
        handleEditClientHub,
        handleAddClientHub,
    } = useHubManagement();

    return (
        <>
            <Header backgroundColor="primary">
                <Typography className={classes.headerTitle} component="h1">
                    Hub Management
                </Typography>
            </Header>
            <Body>
                <Box className={classes.bodyHeader}>
                    <Typography className={classes.intro}>
                        {intl.formatMessage({ id: "hubManagement.intro" })}
                    </Typography>
                    <Box className={classes.toolbox}>
                        <Box className={classes.filtersWrapper}>
                            <Box sx={{ maxWidth: 450, mr: 1 }}>
                                <TextField
                                    size="small"
                                    id="hubname-filter"
                                    name="hubname-filter"
                                    autoComplete="off"
                                    value={inputName}
                                    className={classes.filterByNameInput}
                                    onChange={(event) => handleChangeFilters(event)}
                                    placeholder={intl.formatMessage({ id: "hubManagement.filters.filterByHubName" })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CustomIcon icon={icons.searchInverted} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                            <Box sx={{ minWidth: 210, maxWidth: 210, mr: 1 }}>
                                <FormLabel className={classes.label}>
                                    {intl.formatMessage({ id: "hubManagement.filters.labels.ldPermissions" })}
                                </FormLabel>
                                <Select
                                    multiple
                                    id="ld-permission-filter"
                                    name="ld-permission-filter"
                                    labelId="filter-by-ld-permissions"
                                    value={filters.filter_roles_ld}
                                    className={classes.selectField}
                                    onChange={(event) => handleChangeFilters(event)}
                                    IconComponent={(props) => (
                                        <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                                    )}
                                    renderValue={(selected) => {
                                        const numSelected = selected.length - 1;
                                        const lastSelected = ldPermissionsData.find(
                                            (permission) => permission.name === selected.at(-1)
                                        );
                                        return (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Typography
                                                    className={cn({
                                                        [classes.filterPlaceholderLabel]: lastSelected?.name === "all",
                                                    })}
                                                >
                                                    {lastSelected?.label}
                                                </Typography>
                                                {numSelected > 0 && <Chip size="small" label={`+${numSelected}`} />}
                                            </Box>
                                        );
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            className: classes.selectFilterPaper,
                                        },
                                    }}
                                >
                                    {ldPermissionsData.map((ldPermission) => {
                                        return (
                                            <MenuItem
                                                key={ldPermission.name}
                                                value={ldPermission.name}
                                                className={classes.selectMenuItem}
                                            >
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={filters.filter_roles_ld.includes(ldPermission.name)}
                                                    />
                                                    <ListItemText primary={ldPermission.label} />
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                    <li className={classes.selectMenuClear}>
                                        <ClearFilterButton
                                            disabled={filters.filter_roles_ld.includes("all")}
                                            onClick={() => handleClearFilters("filter_roles_ld")}
                                            startIcon={<CustomIcon icon={icons.circledTimesHollowed} />}
                                        />
                                    </li>
                                </Select>
                            </Box>
                            <Box sx={{ minWidth: 210, maxWidth: 210, mr: 1 }}>
                                <FormLabel className={classes.label}>
                                    {intl.formatMessage({ id: "hubManagement.filters.labels.pacePermissions" })}
                                </FormLabel>
                                <Select
                                    multiple
                                    id="pace-permission-filter"
                                    name="pace-permission-filter"
                                    labelId="filter-by-pace-permissions"
                                    value={filters.filter_roles_pace}
                                    className={classes.selectField}
                                    onChange={(event) => handleChangeFilters(event)}
                                    IconComponent={(props) => (
                                        <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                                    )}
                                    renderValue={(selected) => {
                                        const numSelected = selected.length - 1;
                                        const lastSelected = pacePermissionsData.find(
                                            (permission) => permission.name === selected.at(-1)
                                        );
                                        return (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Typography
                                                    className={cn({
                                                        [classes.filterPlaceholderLabel]: lastSelected?.name === "all",
                                                    })}
                                                >
                                                    {lastSelected?.label}
                                                </Typography>
                                                {numSelected > 0 && <Chip size="small" label={`+${numSelected}`} />}
                                            </Box>
                                        );
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            className: classes.selectFilterPaper,
                                        },
                                    }}
                                >
                                    {pacePermissionsData.map((pacePermission) => {
                                        return (
                                            <MenuItem
                                                key={pacePermission.name}
                                                value={pacePermission.name}
                                                className={classes.selectMenuItem}
                                            >
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={filters.filter_roles_pace.includes(
                                                            pacePermission.name
                                                        )}
                                                    />
                                                    <ListItemText primary={pacePermission.label} />
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                    <li className={classes.selectMenuClear}>
                                        <ClearFilterButton
                                            disabled={filters.filter_roles_pace.includes("all")}
                                            onClick={() => handleClearFilters("filter_roles_pace")}
                                            startIcon={<CustomIcon icon={icons.circledTimesHollowed} />}
                                        />
                                    </li>
                                </Select>
                            </Box>
                            <Box>
                                {!disableClearAll && (
                                    <ClearFilterButton
                                        onClick={() => handleClearFilters("all")}
                                        startIcon={<CustomIcon icon={icons.circledTimesHollowed} />}
                                    />
                                )}
                            </Box>
                        </Box>
                        <NavButton
                            showBackArrow={false}
                            disabled={disableAll}
                            onClick={handleAddClientHub}
                            startIcon={<CustomIcon icon={icons.plus} />}
                            translationId="hubManagement.button.addClientHub"
                        />
                    </Box>
                </Box>
                <Box ml={1.5}>
                    <TablePagination
                        currentPage={hubs.meta.page_no}
                        totalPages={hubs.meta.pages}
                        pageValue={inputPage}
                        onPageChange={handlePageChange}
                        pageSize={hubs.meta.page_size}
                        totalHits={hubs.meta.hits}
                        handleInputChange={handleInputChange}
                        handleOnBlur={handleOnBlur}
                        handleOnKeyDown={handleOnKeyDown}
                        inputRef={paginationTextInputRef}
                    />
                </Box>
                <TableContainer sx={{ mb: 6 }}>
                    <Table>
                        <TableHead className={classes.tableHeader}>
                            <TableRow>
                                {tableHeader
                                    .filter((cell) => !cell.isHidden)
                                    .map((cell) => {
                                        return (
                                            <TableCell key={cell.id} sx={cell.sx}>
                                                {!isEmpty(cell.sort) ? (
                                                    <Box className={classes.cellWithSortButton}>
                                                        <SortMenu
                                                            columnId={cell.id}
                                                            currentSortBy={currentSort}
                                                            sortConfig={cell.sort}
                                                            icon={
                                                                <CustomIcon
                                                                    className={classes.sortButton}
                                                                    icon={icons.sortArrows}
                                                                />
                                                            }
                                                            {...(!isEmpty(cell.sort)
                                                                ? {
                                                                      onSort: (order, columnId) =>
                                                                          handleSort(order, columnId),
                                                                  }
                                                                : {})}
                                                            {...(cell.isHidable
                                                                ? {
                                                                      onHideColumn: (ev, columnId) =>
                                                                          handleToggleColumn(ev, columnId),
                                                                  }
                                                                : {})}
                                                        />
                                                        <Typography>
                                                            {cell.name}{" "}
                                                            {cell.id === "name" && hubs.status !== "fetching" && (
                                                                <span>({hubs.meta.hits})</span>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography>{cell.name}</Typography>
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                <TableCell sx={{ width: 30 }} align="center">
                                    <HideColumnsMenu
                                        items={hidableColumns as any}
                                        onClickItem={handleToggleColumn}
                                        icon={<CustomIcon icon={icons.threeDots} sx={{ fontSize: 24 }} />}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody className={classes.tableBody}>
                            {/* Data */}
                            {hubs.status === "done" &&
                                Boolean(hubs.data.length) &&
                                hubs.data.map((clientHub) => (
                                    <TableRow
                                        key={clientHub.id}
                                        onClick={() => handleEditClientHub(clientHub.id)}
                                        sx={{ "&:hover": { cursor: "pointer" } }}
                                    >
                                        <TableCell colSpan={hidableColumns.length - 3}>
                                            <TagName>{clientHub.name}</TagName>
                                        </TableCell>
                                        <TableCell>{clientHub.ldPermission}</TableCell>
                                        <TableCell>{clientHub.pacePermission}</TableCell>
                                        <TableCell data-hidden={visibleColumns.includes("users_count")}>
                                            <ButtonWithCounter
                                                isDisabled={clientHub.usersCount === 0}
                                                fontSize={12}
                                                count={clientHub.usersCount}
                                                colors={["hovered", "secondaryLight"]}
                                                shouldShowIcon={rowId === clientHub.id}
                                                callback={(ev) => handleClickCounter(ev, clientHub.id)}
                                            />
                                        </TableCell>
                                        <TableCell data-hidden={visibleColumns.includes("crm_id")}>
                                            {clientHub.crmUrl && (
                                                <Link
                                                    className={classes.hyperlink}
                                                    href={clientHub.crmUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <CustomIcon icon={icons.hyperlink} />
                                                    <Typography>
                                                        {intl.formatMessage({ id: "hubManagement.table.CRMLink" })}
                                                    </Typography>
                                                </Link>
                                            )}
                                        </TableCell>
                                        <TableCell data-hidden={visibleColumns.includes("created_utc")}>
                                            {format(new Date(clientHub.createdOn), "dd/MM/yyyy")}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip arrow placement="bottom-start" title="Edit Client Hub">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditClientHub(clientHub.id)}
                                                >
                                                    <CustomIcon
                                                        icon={icons.pencil}
                                                        sx={{ fontSize: 16, color: "secondary.main" }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}

                            {/* Fetching */}
                            {hubs.status === "fetching" &&
                                Array.from(Array(6)).map((el, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={hidableColumns.length + 4}>
                                            <Skeleton variant="rounded" animation="wave" />
                                        </TableCell>
                                    </TableRow>
                                ))}

                            {/* No data */}
                            {hubs.status === "done" && Boolean(!hubs.data.length) && (
                                <TableRow>
                                    <TableCell colSpan={hidableColumns.length + 3}>
                                        <Box className={classes.noDataWrapper}>
                                            <img width={400} src={drax_lighthouse_animation} alt="Results Not Found" />
                                            <Typography>We couldn’t find any matches.</Typography>
                                            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                                                Please double check your search or try again.
                                            </Typography>
                                            <Box display="flex" alignItems="center">
                                                <NavButton
                                                    variant="outlined"
                                                    showBackArrow={false}
                                                    onClick={() => handleClearFilters("all")}
                                                    sx={{ mr: 1 }}
                                                    startIcon={<CustomIcon icon={icons.close} />}
                                                >
                                                    Clear Search
                                                </NavButton>
                                                <NavButton
                                                    showBackArrow={false}
                                                    onClick={handleAddClientHub}
                                                    startIcon={<CustomIcon icon={icons.plus} />}
                                                >
                                                    Add New Client Hub
                                                </NavButton>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <SnackBar
                    maxWidth={350}
                    shouldOpen={shouldOpenSnackBar}
                    anchorElement={popOverAnchorEl}
                    handleInfoClose={handleClickCloseSnackbar}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    title={
                        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                            Drax Executive Users ({usersData.length})
                        </Typography>
                    }
                    content={<ListOfEntities title="JOB TITLE" list={usersData} />}
                />
            </Body>
        </>
    );
};

export default HubManagement;
